import api from './api'
import type { RegimeTributario } from '@/types/comissao'
import {
  PoliticaRetencao,
  PoliticaRetencaoRequest,
  RetencoesSugeridas,
  TributoRetencao,
} from '@/types/politicaRetencao'

/**
 * Retenções da NF de comissão (AN-PP5 §4, §6) — `PoliticaRetencaoNfController`.
 *
 * Ler (política e sugestão): back-office ou FINANCEIRO, nunca a persona consultor (403). Escrever:
 * FINANCEIRO ou ADMIN sem escopo de tenant (dado global). A sugestão sai pronta do backend.
 */
const BASE_URL = '/api/v1/comissoes'

export const politicaRetencaoService = {
  /** Sem `vigenteEm` = histórico inteiro. Ordem do backend: tributo, regime, código, município, versão desc. */
  listar: async (filtro: { tributo?: TributoRetencao; vigenteEm?: string } = {}): Promise<PoliticaRetencao[]> => {
    const params = new URLSearchParams()
    if (filtro.tributo) params.set('tributo', filtro.tributo)
    if (filtro.vigenteEm) params.set('vigenteEm', filtro.vigenteEm)
    const qs = params.toString()
    return await api.get<PoliticaRetencao[]>(`${BASE_URL}/politicas-retencao${qs ? `?${qs}` : ''}`)
  },

  /**
   * PROPÕE nova versão (202, `statusPublicacao` PENDENTE_APROVACAO; V64): só vale — e só fecha a aberta da mesma
   * chave em `vigenciaInicio` — quando OUTRA pessoa FINANCEIRO/ADMIN aprovar. 422: `MUNICIPIO_SO_NO_ISS`,
   * `MUNICIPIO_INVALIDO`, `CODIGO_SERVICO_INVALIDO`, `ALIQUOTA_INCOERENTE`, `ALIQUOTA_DIVERGENTE`,
   * `ALIQUOTA_FORA_DO_INTERVALO`, `ALIQUOTA_ACIMA_DO_TETO`, `DISPENSA_SEM_RETENCAO`, `VIGENCIA_RETROATIVA`,
   * `VIGENCIA_ANTERIOR_A_VIGENTE`, `VIGENCIA_SOBREPOSTA`, `AREA_DONA_INVALIDA`; 409 `PROPOSTA_JA_PENDENTE`,
   * `CONFLITO_CONCORRENCIA`.
   */
  criar: async (request: PoliticaRetencaoRequest): Promise<PoliticaRetencao> => {
    return await api.post<PoliticaRetencao>(`${BASE_URL}/politicas-retencao`, request)
  },

  /**
   * Aprova a proposta (OUTRA pessoa, FINANCEIRO/ADMIN). 403 `APROVACAO_PELO_PROPONENTE` / `APROVADOR_SEM_PAPEL`;
   * 409 `CONFLITO_VERSAO`, `TRANSICAO_PUBLICACAO_INVALIDA`, `VIGENCIA_EXPIRADA_NA_APROVACAO` (o início já passou:
   * proponha de novo); 422 `VIGENCIA_ANTERIOR_A_VIGENTE`, `VIGENCIA_SOBREPOSTA`.
   */
  aprovar: async (id: string, versaoRegistro?: number | null): Promise<PoliticaRetencao> => {
    return await api.post<PoliticaRetencao>(`${BASE_URL}/politicas-retencao/${encodeURIComponent(id)}/aprovar`, {
      versaoRegistro: versaoRegistro ?? null,
    })
  },

  /** Rejeita (motivo obrigatório — 422 `MOTIVO_REJEICAO_OBRIGATORIO`; mesmas regras de quem aprova). */
  rejeitar: async (id: string, motivo: string, versaoRegistro?: number | null): Promise<PoliticaRetencao> => {
    return await api.post<PoliticaRetencao>(`${BASE_URL}/politicas-retencao/${encodeURIComponent(id)}/rejeitar`, {
      motivo,
      versaoRegistro: versaoRegistro ?? null,
    })
  },

  /**
   * PP-final: o PRÓPRIO proponente retira a proposta pendente (→ REJEITADA, motivo "Retirada pelo proponente"). 403
   * `RETIRADA_SO_PELO_PROPONENTE`; 409 `TRANSICAO_PUBLICACAO_INVALIDA`.
   */
  retirar: async (id: string): Promise<PoliticaRetencao> => {
    return await api.post<PoliticaRetencao>(`${BASE_URL}/politicas-retencao/${encodeURIComponent(id)}/retirar`)
  },

  /**
   * Só versão ATIVA. `vigenciaFim` exclusivo; ausente = hoje. 409 `POLITICA_RETENCAO_JA_ENCERRADA`,
   * `TRANSICAO_PUBLICACAO_INVALIDA`; 422 `VIGENCIA_INVALIDA`.
   */
  encerrar: async (id: string, vigenciaFim?: string | null): Promise<PoliticaRetencao> => {
    return await api.post<PoliticaRetencao>(
      `${BASE_URL}/politicas-retencao/${encodeURIComponent(id)}/encerrar`,
      { vigenciaFim: vigenciaFim || null }
    )
  },

  /**
   * Sugestão por nota (nada é gravado). `regime` opcional (padrão: leitura do arquivo; sem indicação →
   * 422 `REGIME_TRIBUTARIO_OBRIGATORIO`); `municipioIbge` (7 dígitos) obrigatório para sugerir ISS;
   * sem conferência do arquivo atual → 409 `NF_CONFERENCIA_DESATUALIZADA`.
   */
  sugerir: async (
    notaId: string,
    filtro: { regime?: RegimeTributario | null; municipioIbge?: string | null; codigoServico?: string | null } = {}
  ): Promise<RetencoesSugeridas> => {
    const params = new URLSearchParams()
    if (filtro.regime) params.set('regime', filtro.regime)
    if (filtro.municipioIbge) params.set('municipioIbge', filtro.municipioIbge)
    if (filtro.codigoServico) params.set('codigoServico', filtro.codigoServico)
    const qs = params.toString()
    return await api.get<RetencoesSugeridas>(
      `${BASE_URL}/notas-fiscais/${encodeURIComponent(notaId)}/retencoes-sugeridas${qs ? `?${qs}` : ''}`
    )
  },
}

export default politicaRetencaoService
